/**
 * Module for managing college applications.
 * 
 * Main purpose is to manage a multi-stage SPA that contains a data-driven 
 * complex form definition. This is used within the context of the main 
 * framework app.component
 * 
 * @author Brian Sparling 
 * @copyright Mutara Inc. 2019-2025
 */

import { Component, OnInit, HostListener, Input } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, ParamMap, Router, UrlSegment } from '@angular/router';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { FormArray, FormGroup } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FieldType, FormlyFormOptions } from '@ngx-formly/core';
import { ContextService, LayoutInterface, ServerServiceConfig, LoginData } from '../context.service';
import { StepDefinition } from '../dynamic/dynamic';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ControlDataFinder } from '../dynamic/capture-form/control-util';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';

@Component({
  selector: 'mutara-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  providers: [{
    provide: STEPPER_GLOBAL_OPTIONS,
    useValue: { displayDefaultIndicatorType: false, showErrors: true }
  }],
  standalone: false
})
export class LayoutComponent extends FieldType implements OnInit {
  @Input() dialogData: any;
  login: LoginData | undefined;
  programName: string = '';
  skipHelp: boolean = false;
  formGroup: any = new FormArray([]); // Start empty, this gets rebuilt by the processing logic below
  formOptions: FormlyFormOptions[] | undefined;
  formModel: any = {};
  stepDefinitions: StepDefinition[] | undefined;
  appConfig: ServerServiceConfig | undefined;
  serviceDefinitions: any;
  ui: ControlDataFinder | undefined;
  currentLayout: LayoutInterface | null | undefined;
  selected: number = 0;
  applicationId: string = 'NEW';
  __loading: boolean = false;
  deviceData: DeviceDetectorService | undefined;
  isComplete: boolean = false;
  delegate: number = 0;
  canMove: boolean = true;
  processActions: any[] = [];
  fakeSub : Subscription | undefined;
  isVertical: boolean = false;

  constructor(
    public http: HttpClient,
    public actRoute: ActivatedRoute,
    public router: Router,
    public location: Location,
    public contextService: ContextService
  ) {
    super();
    this.contextService.subscribeTo("applicationInfo", (config: ServerServiceConfig) => this.appConfig = config);
    this.contextService.subscribeTo("orientation", (isVertical: boolean) => this.isVertical = isVertical);
    this.contextService.subscribeTo("selectedData", (data: any) => this.selected = data);
    this.contextService.subscribeTo("deviceValue", (deviceData: any) => this.deviceData = deviceData);
  }
  /** Trick the view to vertical if too small */
  @HostListener('window:resize', [])
  onResize() {
    this.contextService.setOrientation(window.innerWidth <= 950 ? 'vertical' : 'horizontal');
  }

  get debug() : boolean {
    return !environment.production;
  }

  get layout(): LayoutInterface | undefined | null {
    return this.currentLayout;
  }
  get schoolName(): string {
    return this.appConfig?.info?.institution ?? '';
  }
  /** 
   * Called when the component is initialized 
   */
  ngOnInit(): void {
    // Make sure the progress bar is percentage complete 
    this.contextService.setProgressIndeterminate(false);

    // This tells us when the Submit button becomes valid
    this.contextService.subscribeTo("formComplete", (readyState: any) => {
      this.isComplete = readyState.valid;
    });

    // Remember what the application ID is
    this.contextService.subscribeTo("applicationNumber", (id: string) => {
      this.applicationId = id;
    });

    // This may get called from the user or the layout change
    this.contextService.subscribeTo("stepIndex", (layoutStep: LayoutInterface) => {
      if (!!layoutStep && !!this.currentLayout && this?.currentLayout?.layoutId == layoutStep?.layout.layoutId) {
        this.selected = this.stepDefinitions?.findIndex((stepDefn) => stepDefn.step == layoutStep.step) ?? 0;
      }
    });

    this.fakeSub = this.contextService.subscribeTo('FAKESUBMIT', (tryPersist: boolean) => {
      if (tryPersist) {
        this.isComplete = true;
        if ( this.persistData() ) {
          this.isComplete = false;
        }
      }
    });

    // Handle things differently if we are embedded in a dialog 
    if (!this.dialogData) {
      // This ensures that a login is present changes the page layout
      this.contextService.subscribeTo("loginData", (login: LoginData) => {
        this.login = login;
        if (this.login?.username == '') {
          this.stepDefinitions?.forEach(step => {
            clearInterval(step.timer);
            step.timer = null;
          });
          // this.stepDefinitions = [];
        } else {
          //this.clearLayout();
          //this.currentLayout = null;
        }
        if ( !environment.production ) { console.log( 'Layout responds to Login %O', login ); }
        if (!!login.defaultLayout) {
          login.defaultLayout = this.contextService.findLayout(login.defaultLayout.layoutKey);
          this.contextService.setLayoutId(login.defaultLayout);
        } else {
          this.contextService.setLayoutId(this.currentLayout);
        }
      });

      // When the layout changes, this is where the magic happens
      this.contextService.subscribeTo("layoutIdentity", (layout: LayoutInterface) => {
        if (!layout) {
          this.clearLayout();
          return;
        }

        this.currentLayout = layout;
        // this needs to be a constant
        this.contextService.showProgress((layout?.defaultRole ?? 0) == 0);
        this.loadLayout();
      });

      // Load the program specifics, then pass control 
      this.actRoute.paramMap.subscribe((params: ParamMap) => {
        const status = params.get('status'), token = params.get('token'), url: UrlSegment[] = this.actRoute.snapshot.url;
        if (!!status && !!token) {// Called by the 3rd party payments
          /// Re-establish the session and get our layout and step back
          console.log( '\n\n.....Re-establish session from token %O....\n\n\n', token);
          this.http.post('$service/token-session', { token: token }).subscribe({
            error: (errorData: any ) => {
              console.error('Unable to reestablish session %O', errorData);
            },
            next: (data: any) => {
              console.log('Reestablished session with session: %O %O', data, this.currentLayout);
              if ( data.layout ) {
                this.location.replaceState(url.slice(0, -2).toString(), '');
//                this.currentLayout = data.layout;
                // this prevents the token from getting in the way
//                this.contextService.layout = data.layout;
                data.layout.tab = data.stepName
                this.contextService.setLayoutId(data.layout);
                //this.contextService.setStepIndex(data.layout,data.stepName);
              } else {
                console.log('Data returned from token %O', data);
              }
            }
          });
        } else { // This is if we are called using the application/
          this.skipHelp = '_restart' == token;
          // if (!!program) {
          //   this.programName = this.contextService.toCamelCase(this.contextService.toWords(program) ?? []);
          //   this.contextService.changeMessage(`Applying to the ${this.programName} Program`);
          //   //this.contextService.setNavigationSource(this.programName);
          //   this.currentLayout = this.contextService.findLayout(program);
          // }
        }
      });
    } else { // Handle being an embedded application here
      this.currentLayout = this.contextService.findLayout(this.dialogData.layoutKey);
      this.login = this.dialogData;
      // this.selected = <number>this.currentLayout.tab ?? 0;
      this.delegate = this.dialogData.delegateAs;
      this.contextService.setDelegateLayout(this.currentLayout);
      this.loadLayout();
    }
  }
  get actionDefinition() { return this.currentLayout?.extra?.$action; }

  $clickedButton(index: number) {
    let buttonDefn = this.actionDefinition.buttons[index];
    console.log('Firing %O', buttonDefn);
    if (buttonDefn.type != 'submit') {
      this.router.navigate([buttonDefn.action], {replaceUrl: false});
    }
  }
  /**
   * Method to build the page based on the requested layout
   */
  loadLayout(stepName: string | undefined = undefined) {
    this.clearLayout();
    if (!!this.currentLayout && this.currentLayout?.layoutId !== undefined) {
      if ( !environment.production ) { console.log( 'Loading layout %O', this.currentLayout); }
      // set up the submission activities
      try {
        this.processActions = (typeof this.currentLayout.submitRoute === 'object') ? <any>this.currentLayout.submitRoute : JSON.parse(this.currentLayout.submitRoute);
      } catch (e) {
        this.processActions.push({ service: this.currentLayout.submitRoute });
      }
      // When the layout is loaded, get all of the individual pages to load
      this.http.post('$service/steps', { layoutId: this.currentLayout.layoutId, delegateAs: this.delegate }).subscribe({
        error: (httpError: HttpErrorResponse) => {
          if (httpError.status > 399) {
            this.contextService.changeMessage(`Server Error ${httpError.statusText}`);
          }
        },
        next: (resultData: any) => {
          this.contextService.showProgress(resultData.steps);
          this.contextService.setProgressIndeterminate(false);
          if (resultData?.steps?.length > 0) {
            this.__processStepDefinitions(resultData);
            if (!!stepName) {
              this.contextService.setStepIndex(this.layout, stepName);
            }
          }
        }
      });
    }
    this.setupSubmissionSteps();
  }
  /**
   * The is the end state service processing steps
   */
  setupSubmissionSteps() {
    // Here we do the processing of the submission sent by message
    this.contextService.subscribeTo("processSubmit", (action: any) => {
      if (!!action) {
        let current = action.shift();
        if (!!current)
          this.http.post('$service/' + current.service, Object.assign(current.parameters ?? {}, {
            layoutId: this.currentLayout?.layoutId,
            device: Object.assign(this.deviceData?.getDeviceInfo() ?? {}, { clientIp: this.contextService.clientIp })
          })).subscribe(
            {
              error: (err) => {
                this.contextService.changeMessage('Error submitting application');
                this.__loading = false;
                return err;
              },
              next: (results: any) => {
                this.__loading = false;
                if (!!this.delegate) {
                  this.contextService.revertDelegate();
                } else {
                  this.contextService.changeMessage(results.message ?? 'Application Submitted');
                }
                if (!!results.parameters) {
                  if (action.length > 0) {
                    action[0].parameters = Object.assign(action[0]?.parameters ?? {}, action.parameters, results.parameters);
                  }
                }
                if (action.length > 0) {
                  this.contextService.nextMessage("processSubmit", action);
                } else {
                  console.log('Switch to status %O %O', results,this.contextService.getMessageValue('loginData') );
                  this.router.navigate(['/status'], {replaceUrl: true, state: { token : this.contextService.getMessageValue('_token'), login : this.contextService.getMessageValue('loginData')}});
                }
              }
            });

      }
    });
  }

  /**
   * This uses the step service of the page definition to provide the layout of the steps
   * and the options. It also provides the generated forms the binding points to the JSON
   * Formly model and data. This ensures that the form can access the entire dataset for the 
   * form to manage hide/show of steps, fields, etc. 
   * 
   * The underlying model structure is dynamic so the code needs a mechanism to lookup values/fields 
   * that is independent of the rigid model structure 
   * 
   * @param resultData step definitions returned by the service 
   */
  __processStepDefinitions(resultData: any) {
    this.serviceDefinitions = resultData;
    if (!!resultData) {
      if (!!(this.stepDefinitions = resultData.steps)) {
        this.formGroup = new FormArray(this.stepDefinitions.map(() => new FormGroup({})));
        this.formOptions = this.stepDefinitions.map(() => <FormlyFormOptions>{});
        this.stepDefinitions.forEach((item: StepDefinition, index) => {
          item.isLoaded = false;
          if (!this.formModel[item.step]) {
            this.formModel[item.step] = {};
          }
          if (this.currentLayout?.tab && (item.step == this.currentLayout.tab || index == this.currentLayout.tab)) {
            this.contextService.setStepIndex(this.currentLayout, item.step, null);
            //this.steps[index].wasTouched = true;
          }
          // Setup the information that is used for global information
          Object.assign(item, {
            totalRequired: 0,
            invalidValues: [],
            fieldDefinitions: [],
            dataModel: this.formModel[item.step],
            domForm: this.formGroup.at(index),
            formlyOptions: this.formOptions?.[index],
            endUser: !!resultData.mustFulfill,
            refreshInterval: item.refreshInterval ?? resultData.refreshInterval,
            refreshCount: resultData.refreshCount,
          });
        });
        // WIP -- this.ui = new ControlDataFinder(  this.stepDefinitions );
      } else {
        console.log('No steps for layout definition %O', resultData);
      }
    }
  }

  /**
   * reset the selected layout to display
   * This should trigger the UI to fall back to the action header
   */
  clearLayout() {

    this.serviceDefinitions = null;
    this.stepDefinitions = [];
    this.formGroup = null;
    this.formOptions = [];
    this.formModel = {};
    this.selected = 0;
    this.contextService.setApplicationNumber('NEW');
    this.contextService.showProgress(false);
    this.contextService.nextMessage('readyToReceive', false);
    this.contextService.setIsValid({ valid: false, label: null });
    this.processActions = [];
  }

  isOptional(step: StepDefinition): boolean {
    return !(this.isRequired(step) && this.isStepVisible(step));
  }

  _evaluateValue(toEvaluate: string, defaultValue: boolean = true): boolean {
    let isTrue = defaultValue;
    try {
      if (!!toEvaluate) {
        isTrue = (toEvaluate.toLowerCase() == 'true' || toEvaluate == '1' || eval('this.' + toEvaluate));
      }
    } catch (error) {
      console.debug('Unable to evaluate: %O', toEvaluate);
    }
    return isTrue;
  }
  /**
   *  Trigger the setting of the index on the Back/Next/Icon 
   */
  setStep($event: any) {
    if (this.canMove) {
      this.contextService.setStepIndex(this.currentLayout, $event.selectedIndex, null);
    }
    //this.steps[this.selected].wasTouched = true;
  }
  isStepVisible(step: any): boolean {
    return this._evaluateValue(step.isVisible, true);
  }
  isRequired(step: any): boolean {
    return this._evaluateValue(step.isRequired, true);
  }

  // BMS NOTE: This is a formatter for the time sliders (abandon for now)
  formatHours(value: number) { return String(value) + 'hr'; }

  // BMS NOTE: check of loggedin value 
  get isLoggedIn() {
    return !!this.login && this.login?.username != '';
  }
  get appTitle() {
    return this.programName != '' ? `Welcome to the ${this.programName} Program!` : 'Getting Started';
  }

  get imageUrl() {
    return this.programName != '' ? `/assets/${this.programName.replace(/\ /g, '_').toLowerCase()}.png` : this.appConfig?.registrationImage ?? '/assets/goals.png';
  }
  get hasRegistration(): boolean {
    let reg: boolean = !!this.appConfig?.layoutDefinitions?.find((defn) => defn.layoutId == 0);
    return reg;
  }

  /**
   * Called when the submission is pressed. This kicks off the set of services necessary to process
   * the application. 
   * 
   * Example: payment service, submission to SOR, forward documents
   * 
   * @param $event 
   * @returns 
   */
  persistData(): boolean {
    if (!this.isComplete) {
      return false;
    }
    // Only post if this is not currently submitted (no id)
    if (['NEW', 'ERROR'].includes(this.applicationId ?? 'NEW')) { // Can resubmit errors
      this.contextService.setApplicationNumber('PROCESS');
      this.__loading = true;
      this.contextService.nextMessage("processSubmit", [...this.processActions]);
    }
    return true;
  }
};